import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

// Material UI Components
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";

import { BasicDetailsServiceType } from "../../components/POSList/posUtils/PosAccessUtils";
import { ALERT_TYPES, APP_ROUTES, NEW_PATH_SELECTED, PHARMACY, POS_CATEGORY_CONSTANT, SERVICE_TYPES, VALIDATE_POS_NUMBER } from "../../config/constants";
import { selectAllCountries, selectPosTypes } from "../../config/redux/configurationsSlice";
import { isObjectEmpty } from "../../utils/helperFunctions";
import CustomAlert from "../common/CustomAlert/CustomAlert";
import Loader from "../common/Loader/Loader";
import PageTitle from "../common/PageTitle/PageTitle";
import PrimaryButton from "../common/PrimaryButton/PrimaryButton";
import SelectBox from "../common/SelectBox/SelectBox";
import OverlayLoader from "../common/Loader/OverlayLoader";
import SwitchButton from "../common/SwitchButton/SwitchButton";
import TextBox from "../common/TextBox";
import { createPOS, deletePOS, fetchPOSDetailsByPOSNo, updatePOS } from "../CreatePOS/redux/posSlice";
import { selectUserDetails } from "../Login/redux/userSlice";
import { basicDetailsStyles } from "./BasicDetailsStyle";
import { posDetailsInRequiredFormat } from "./Helper";
import { isArrayValid, isEmptyOrNull } from "../../utils/DataUtils";
import useCommonDataConfig from "../../config/redux/useCommonDataConfig";
import { STORE_SERVICE_BUTTON_LABELS, STORE_SERVICE_LABELS, STORE_SERVICE_MESSAGES, STORE_SERVICE_SUCCESS_MESSAGES } from "../../components/common/App/app-constant";
import { is_user_maf_admin } from "../../utils/ApplicationUtils";
import DeletePosDialog from "./Utils/DeletePosDialog";

const isValidLatitudeOrLongitudeValue = (val = 0, min = -180, max = 180) => {
  return val >= min && val <= max;
};

function Basic() {
  const { posNo } = useParams();
  const classes = basicDetailsStyles();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { servicePropositionList } = useCommonDataConfig();
  const posTypes = useSelector(selectPosTypes);
  const countries = useSelector(selectAllCountries);
  const userInfo = useSelector(selectUserDetails);
  const { currentCountry: { name: currentCountryName } } = useSelector((state) => state.appConfig);
  const { loading, fetching, posDetails: savedPOSDetails } = useSelector((state) => state.pos);

  const isEditMode = !!posNo || !isObjectEmpty(savedPOSDetails);

  const [appState, setAppState] = useState({ openDeletePosDialog: false });
  const [showOverLoaderState, setShowOverLoaderState] = useState(false);
  const [serviceType, setServiceType] = useState("");
  const [posDetails, setPosDetails] = useState({
    storeName: "",
    posType: POS_CATEGORY_CONSTANT.SUPERMARKET,
    posNumber: "",
    billingPos: "",
    fulfilledBy: "",
    deliveredBy: "",
    fulfillmentSystem: "",
    virtualPos: false,
  });
  const [posAddress, setPosAddress] = useState({
    contactId: 0,
    contactName: "",
    email: "",
    contactNo: "",
    country: currentCountryName,
    region: "",
    area: "",
    iso: currentCountryName,
    addressLine1: "",
    addressLine2: "",
    street: "",
    latitude: "",
    longitude: "",
  });
  const [errors, setErrors] = useState({
    storeName: "",
    posType: "",
    posNumber: "",
    contactName: "",
    email: "",
    contactNo: "",
    country: "",
    region: "",
    area: "",
    iso: "",
    addressLine1: "",
    addressLine2: "",
    street: "",
    latitude: "",
    longitude: "",
    serviceType: "",
  });

  const handlePosDetailsChange = (event) => {
    const { name, value } = event.target;
    setPosDetails((details) => ({ ...details, [name]: value }));
    if (name === "posType") {
      setServiceType("");
    }
    if (value) {
      setErrors((err) => ({ ...err, [name]: "" }));
    }
  };

  /**
   * Method to handle product type selection
   * @param {object} event
   */
  const handleServiceType = (event) => {
    const { value } = event.target;

    let alreadySelectedServiceTypes = serviceType ? serviceType.split(", ") : [];

    if (alreadySelectedServiceTypes.includes(value)) {
      alreadySelectedServiceTypes = alreadySelectedServiceTypes.filter((type) => type !== value);
    } else {
      alreadySelectedServiceTypes.push(value);
    }

    setServiceType(alreadySelectedServiceTypes.join(", "));

    setErrors({ ...errors, serviceType: "" });
  };

  const handlePosAddressChange = (event) => {
    if (!isEmptyOrNull(event?.target)) {
      const { name, value } = event.target;
      setPosAddress((prevAddress) => ({ ...prevAddress, [name]: value }));
      setErrors((prevErrors) => {
        if (!isEmptyOrNull(value)) {
          const newErrors = { ...prevErrors };
          if (newErrors[name]) { // Only delete if the error actually exists
            delete newErrors[name];
          }
          return newErrors;
        }
        return prevErrors; // No change to errors if value is still empty
      });
    }
  }

  const getPOSNumberValidationErrorMsg = () => {
    const { posNumber, posType } = posDetails;

    if (!posNumber) return STORE_SERVICE_MESSAGES.POS_NUMBER_EMPTY;

    const isNonPharmacy = posType !== PHARMACY;
    if (isNonPharmacy) {
      if (!VALIDATE_POS_NUMBER.test(posNumber)) {
        return STORE_SERVICE_MESSAGES.POS_TYPE_SPECIAL_CHARACTER;
      }
    }
    return "";
  };

  const isValidEmail = (email) =>
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,})$/i.test(email);

  const isValidName = (name) => /^[a-z0-9 ]+$/i.test(name);

  const validateLatitude = (lat) =>
    isValidLatitudeOrLongitudeValue(lat, -90, 90) || STORE_SERVICE_MESSAGES.LATITUDE_VALUE_ERROR;

  const validateLongitude = (lng) =>
    isValidLatitudeOrLongitudeValue(lng) || STORE_SERVICE_MESSAGES.LONGITUDE_VALUE_ERROR;

  const addErrorIfInvalid = (errors, field, condition, message) => {
    if (condition) errors[field] = message;
  };

  const validateContactFields = (errors) => {
    if (posAddress.contactName && !isValidName(posAddress.contactName)) {
      errors.contactName = STORE_SERVICE_MESSAGES.POS_NAME_EMPTY;
    }

    if (posAddress.email && !isValidEmail(posAddress.email)) {
      errors.email = STORE_SERVICE_MESSAGES.POS_EMAIL_EMPTY;
    }

    if (posAddress.contactNo && posAddress.contactNo.length < 10) {
      errors.contactNo = STORE_SERVICE_MESSAGES.PHONE_NUMBER_EMPTY;
    }
  };

  const validateLatLong = (errors) => {
    if (!posAddress.latitude) {
      errors.latitude = STORE_SERVICE_MESSAGES.LATITUDE_EMPTY;
    } else {
      const latError = validateLatitude(posAddress.latitude);
      if (latError !== true) errors.latitude = latError;
    }

    if (!posAddress.longitude) {
      errors.longitude = STORE_SERVICE_MESSAGES.LONGITUDE_EMPTY;
    } else {
      const lngError = validateLongitude(posAddress.longitude);
      if (lngError !== true) errors.longitude = lngError;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    addErrorIfInvalid(newErrors, "storeName", !posDetails.storeName, "Please enter store name");
    addErrorIfInvalid(newErrors, "posType", !posDetails.posType, "Please enter POS type");
    const posNumberError = getPOSNumberValidationErrorMsg();
    if (posNumberError) newErrors.posNumber = posNumberError;
    addErrorIfInvalid(newErrors, "addressLine1", !posAddress.addressLine1, "Please enter your address");
    addErrorIfInvalid(newErrors, "serviceType", !serviceType, "Please select a service type!");
    addErrorIfInvalid(newErrors, "region", !posAddress.region, "Please enter Region/City name");
    addErrorIfInvalid(newErrors, "area", !posAddress.area, "Please enter area name");
    validateLatLong(newErrors);
    validateContactFields(newErrors);
    setErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
    return Object.values(newErrors).every((val) => !val);
  };

  const isServiceTypeDisabled = (serviceTypeId) => {
    if (isEditMode && !isEmptyOrNull(savedPOSDetails?.services)) {
      return savedPOSDetails.services?.some(item => item?.id && item.id === serviceTypeId);
    }
    return false;
  }

  const fetchPOSDetails = useCallback(async () => {
    if (posNo && servicePropositionList?.length) {
      dispatch(fetchPOSDetailsByPOSNo(posNo));
    }
  }, [dispatch, posNo, servicePropositionList]);

  const handleSubmit = async () => {
    if (validateForm()) {
      const requestPayload = posDetailsInRequiredFormat(posDetails, serviceType, posAddress, currentCountryName);
      const { payload } = await dispatch(createPOS(requestPayload));
      if (payload?.posCreatedId && !isEmptyOrNull(payload?.services) && isArrayValid(payload.services)) {
        CustomAlert(ALERT_TYPES.SUCCESS, STORE_SERVICE_SUCCESS_MESSAGES.POS_CREATED);
        if (posDetails.posType === SERVICE_TYPES.FBC) {
          navigate(`${APP_ROUTES.COMPLETE_SETUP}/${posNo}`);
        } else {
          navigate(`${APP_ROUTES.CREATE_POS}${APP_ROUTES.SERVICE_AND_SLOTS}/${payload.services[0].name.toLowerCase()}/${posNo}`);
        }
      }

      if (payload?.message) {
        CustomAlert(ALERT_TYPES.ERROR, payload.message);
      }
    }
  };

  const handlePOSUpdate = async () => {
    if (validateForm()) {
      setShowOverLoaderState(true);
      const requestPayload = posDetailsInRequiredFormat(posDetails, serviceType, posAddress, currentCountryName);
      const { payload } = await dispatch(updatePOS(requestPayload))
      if (!isEmptyOrNull(payload?.id)) {
        CustomAlert(ALERT_TYPES.SUCCESS, STORE_SERVICE_SUCCESS_MESSAGES.POS_UPDATED);
      }
      if (payload?.message) {
        CustomAlert(ALERT_TYPES.ERROR, payload.message);
      }
      setShowOverLoaderState(false);
    }
  };

  const handleServiceTypeCheckBox = (serviceType, typeId) => {
    let serviceTypeArray = serviceType ? serviceType.split(", ") : [];
    let typeyIdStr = typeId ? typeId.toString() : "";
    return serviceTypeArray.indexOf(typeyIdStr) !== -1;
  };

  const handleOpenDeleteDialog = () => {
    setAppState({ ...appState, openDeletePosDialog: true });
  }

  const handleCloseDeleteDialog = () => {
    setAppState({ ...appState, openDeletePosDialog: false });
  }

  const handleDeletePos = async () => {
    const { payload } = await dispatch(deletePOS(posDetails.posNumber)); //NOSONAR
    setAppState({ ...appState, openDeletePosDialog: false });
    if (payload?.success) {
      CustomAlert(ALERT_TYPES.SUCCESS, `POS ${posDetails?.posNumber ?? ""} deleted successfully`);
      let url = `${APP_ROUTES.POS_LIST}`;
      localStorage.setItem(NEW_PATH_SELECTED, url);
      navigate(url);
    } else {
      CustomAlert(ALERT_TYPES.ERROR, payload?.message ?? STORE_SERVICE_MESSAGES.DELETE_POS_ERROR_MESSAGE);
    }
  }

  const renderServiceTypes = () => {
    return (
      <BasicDetailsServiceType
        classes={classes}
        userInfo={userInfo}
        serviceTypes={servicePropositionList}
        serviceType={serviceType}
        handleServiceType={handleServiceType}
        isServiceTypeDisabled={isServiceTypeDisabled}
        errors={errors}
        posType={posDetails.posType}
        handleServiceTypeCheckBox={handleServiceTypeCheckBox}
      />
    )
  }

  useEffect(() => {
    if (isEditMode) {
      fetchPOSDetails();
    }
  }, [isEditMode, fetchPOSDetails]);

  useEffect(() => {
    if (savedPOSDetails && Object.keys(savedPOSDetails).length) {
      const { posContacts, posAddress: savedPOSAddress, serviceType: savedServiceType } = savedPOSDetails;
      setPosDetails((posDetails) => ({
        ...posDetails,
        storeName: savedPOSDetails.storeName,
        posType: savedPOSDetails.posType,
        posNumber: savedPOSDetails.posNo,
        billingPos: savedPOSDetails.billingPos || "",
        fulfilledBy: savedPOSDetails.fulfilledBy || "",
        deliveredBy: savedPOSDetails.deliveredBy || "",
        fulfillmentSystem: savedPOSDetails.fulfillmentSystem || "",
        virtualPos: savedPOSDetails.virtualPos || false,
      }));
      setPosAddress((posAddress) => ({
        ...posAddress,
        contactId: posContacts[0].id || 0,
        contactName: posContacts[0].name || "",
        email: posContacts[0].email || "",
        contactNo: posContacts[0].phoneNumber,
        ...savedPOSAddress,
      }));
      setServiceType(savedServiceType?.reduce((acc, value) => [...acc, value?.serviceTypeId], []).join(", "));
    }
  }, [savedPOSDetails]);

  return (
    <>
      <div className={classes.mainContainer}>
        <PageTitle title={isEditMode ? STORE_SERVICE_BUTTON_LABELS.UPDATE_POS : STORE_SERVICE_BUTTON_LABELS.CREATE_NEW_POS} />
        {fetching && <Loader />}
        <OverlayLoader loading={showOverLoaderState} />
        {!fetching && (
          <>
            <Card className={classes.cards}>
              <CardContent>
                <Typography className={classes.cardTitle}>{`${STORE_SERVICE_LABELS.PRIMARY_INFORMATION}`}</Typography>
                <Grid container spacing={1} className={classes.container}>
                  <Grid item xs={3} className={classes.gridSection}>
                    <TextBox
                      label={`${STORE_SERVICE_LABELS.POS_NUMBER}`}
                      name="posNumber"
                      value={posDetails.posNumber}
                      handleChange={handlePosDetailsChange}
                      error={!!errors.posNumber}
                      helperText={errors.posNumber}
                      textBoxId="POSNumber"
                      disabled={isEditMode || !isObjectEmpty(savedPOSDetails)}
                    ></TextBox>
                  </Grid>
                  <Grid item xs={3} className={classes.gridSection}>
                    <TextBox
                      label={`${STORE_SERVICE_LABELS.POS_NAME}`}
                      name="storeName"
                      value={posDetails.storeName}
                      handleChange={handlePosDetailsChange}
                      error={!!errors.storeName}
                      helperText={errors.storeName}
                      textBoxId="storename"
                    />
                  </Grid>
                  <Grid item xs={3} className={classes.gridSection}>
                    {posTypes && (
                      <SelectBox
                        name="posType"
                        label={`${STORE_SERVICE_LABELS.POS_TYPE}`}
                        menuItems={posTypes}
                        value={posDetails.posType}
                        handleChange={handlePosDetailsChange}
                        error={!!errors.posType}
                      />
                    )}
                    {errors?.posType ? <div className={classes.error}>{errors.posType}</div> : null}
                  </Grid>
                  <Grid item xs={3} className={`${classes.gridSection} ${classes.switchButtonGrid}`}>
                    <SwitchButton
                      leftLabel="Virtual POS"
                      checked={posDetails.virtualPos}
                      handleChange={(e) => setPosDetails((details) => ({ ...details, virtualPos: e.target.checked }))}
                    />
                  </Grid>
                </Grid>
                {renderServiceTypes()}
              </CardContent>
            </Card>
            <Card className={classes.cards}>
              <CardContent>
                <Typography className={classes.cardTitle}>{`${STORE_SERVICE_LABELS.ADDRESS_INFORMATION}`}</Typography>

                <div className={classes.customerDetailsContainer}>
                  <Typography className={classes.smallTitle}>{`${STORE_SERVICE_LABELS.CUSTOMER_SERVICE_DETAILS}`}</Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <TextBox
                        label={`${STORE_SERVICE_LABELS.NAME}`}
                        name="contactName"
                        value={posAddress.contactName}
                        handleChange={handlePosAddressChange}
                        error={!!errors.contactName}
                        helperText={errors.contactName}
                        inputProps={{ maxLength: 100 }}
                        textBoxId="storeNameAddressTextBox"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextBox
                        fullWidth
                        variant="outlined"
                        handleChange={handlePosAddressChange}
                        value={posAddress.email}
                        error={!!errors.email}
                        helperText={errors.email}
                        textBoxId="email"
                        label={`${STORE_SERVICE_LABELS.EMAIL}`}
                        name="email"
                        type="email"
                      ></TextBox>
                    </Grid>
                    <Grid item xs={4}>
                      <TextBox
                        fullWidth
                        variant="outlined"
                        handleChange={handlePosAddressChange}
                        value={posAddress.contactNo}
                        inputProps={{ minlength: 15, maxLength: 15 }}
                        error={!!errors.contactNo}
                        helperText={errors.contactNo}
                        textBoxId="contact-no"
                        label={`${STORE_SERVICE_LABELS.PHONE_NUMBER}`}
                        name="contactNo"
                        type="number"
                      />
                    </Grid>
                  </Grid>
                </div>

                <div className={classes.storeDetailsContainer}>
                  <Typography className={classes.smallTitle}>{`${STORE_SERVICE_LABELS.LOCATION_DETAILS}`}</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4} className={classes.addressSectionFirstField}>
                      <SelectBox name="country" label="Country" menuItems={countries} value={posAddress.country} handleChange={handlePosAddressChange} disabled />
                    </Grid>
                    <Grid item xs={4} className={classes.addressSectionSecondField}>
                      <TextBox
                        name="region"
                        label="City"
                        value={posAddress.region}
                        handleChange={handlePosAddressChange}
                        inputProps={{ maxLength: 100 }}
                        error={!!errors.region}
                        helperText={errors.region}
                        required
                      />
                    </Grid>
                    <Grid item xs={4} className={classes.addressSectionFirstField}>
                      <TextBox
                        name="area"
                        label="Area"
                        value={posAddress.area}
                        handleChange={handlePosAddressChange}
                        inputProps={{ maxLength: 100 }}
                        error={!!errors.area}
                        helperText={errors.area}
                        required
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} className={classes.addressSectionFields}>
                    <Grid item xs={4}>
                      <TextBox
                        name="addressLine1"
                        label="Address Line 1"
                        value={posAddress.addressLine1}
                        handleChange={handlePosAddressChange}
                        inputProps={{ maxLength: 100 }}
                        error={!!errors.addressLine1}
                        helperText={errors.addressLine1}
                        required
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <TextBox
                        name="addressLine2"
                        label="Address Line 2"
                        value={posAddress.addressLine2}
                        handleChange={handlePosAddressChange}
                        inputProps={{ maxLength: 100 }}
                        error={!!errors.addressLine2}
                        helperText={errors.addressLine2}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <TextBox name="street" label="Street" value={posAddress.street} handleChange={handlePosAddressChange} inputProps={{ maxLength: 50 }} required={false} />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} className={classes.addressSectionFields}>
                    <Grid item xs={4} className={classes.addressSectionFirstField}>
                      <TextBox
                        textBoxId="latitude"
                        label="Latitude"
                        name="latitude"
                        type="number"
                        handleChange={handlePosAddressChange}
                        value={posAddress.latitude}
                        error={!!errors.latitude}
                        helperText={errors.latitude}
                        required
                      />
                    </Grid>

                    <Grid item xs={4} className={classes.addressSectionSecondField}>
                      <TextBox
                        name="longitude"
                        label="Longitude"
                        type="number"
                        textBoxId="longitude"
                        handleChange={handlePosAddressChange}
                        value={posAddress.longitude}
                        error={!!errors.longitude}
                        helperText={errors.longitude}
                        required
                      />
                    </Grid>
                  </Grid>
                </div>
              </CardContent>
            </Card>
            <Grid container className={classes.buttonContainer} justifyContent="space-between" alignItems="center">
              <Grid item>
                {isEditMode ? (
                  <Link to={posNo ? `${APP_ROUTES.COMPLETE_SETUP}/${posNo}` : APP_ROUTES.POS_LIST} className={classes.linkstyle}>
                    <PrimaryButton buttonLabel={`${STORE_SERVICE_BUTTON_LABELS.BACK_TO_POS}`} buttonVariant="outlined" />
                  </Link>
                ) : (
                  <Link to={`${APP_ROUTES.POS_LIST}`} className={classes.linkstyle}>
                    <PrimaryButton buttonLabel={`${STORE_SERVICE_BUTTON_LABELS.CANCEL}`} buttonVariant="outlined" />
                  </Link>
                )}
              </Grid>
              <Grid item>
                <Grid container spacing={1} justifyContent="flex-end">
                  {isEditMode ? (
                    <>
                      {is_user_maf_admin(userInfo) && (
                        <Grid item>
                          <PrimaryButton
                            buttonLabel={STORE_SERVICE_BUTTON_LABELS.DELETE_POS}
                            onClick={() => handleOpenDeleteDialog()}
                            className={classes.deleteButton}
                            disabled={loading}
                          />
                        </Grid>
                      )}
                      <Grid item>
                        <PrimaryButton
                          buttonLabel={loading ? STORE_SERVICE_BUTTON_LABELS.CURRENT_LOADING : STORE_SERVICE_BUTTON_LABELS.UPDATE_POS}
                          onClick={handlePOSUpdate}
                          disabled={loading}
                        />
                      </Grid>
                    </>
                  ) : (
                    <Grid item>
                      <PrimaryButton
                        buttonLabel={loading ? STORE_SERVICE_BUTTON_LABELS.CURRENT_LOADING : STORE_SERVICE_BUTTON_LABELS.SAVE_AND_PROCEED}
                        onClick={handleSubmit}
                        disabled={loading}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </div>
      {appState.openDeletePosDialog && !isEmptyOrNull(posDetails.posNumber) && (
        <DeletePosDialog
          open={appState.openDeletePosDialog}
          onClose={() => handleCloseDeleteDialog()}
          posNumber={posDetails.posNumber}
          onDeletePos={handleDeletePos}
          classes={classes}
        />
      )}
    </>
  );
}

export default Basic;

